import { Autocomplete, FeedbackMessage, TextField } from '@gemini/ui';
import { useEffect, useRef, useState } from 'react';
import { Box, useBreakpointValue } from '@gemini/core';
import { useLocale, useTranslation } from '@aviv-unified-frontend/runtime/i18n';
// eslint-disable-next-line import/no-extraneous-dependencies
import debounce from 'lodash.debounce';
import { ArrowRightIcon } from '@gemini/icons';

import {
    useAppContext,
    useAppDispatch,
} from '@/frontend/src/contexts/appContext';
import { useBreakpoint } from '@/frontend/src/hooks/useBreakpoint';
import {
    fetchAddresses,
    fetchHouseNumber,
    getCountries,
} from '@/frontend/src/helpers/geo';
import type {
    IAutocompleteItem,
    IAutocompleteParams,
    IGeoMatchItem,
} from '@/frontend/src/types/geo';
import { EPlaceTypes } from '@/frontend/src/types/geo';
import {
    EActionType,
    EActiveView,
    EParentApp,
} from '@/frontend/src/types/context';
import { feedbackApiRequest } from '@/frontend/src/services/geoApiRequest';
import { initialState } from '@/frontend/src/contexts/initialState';
import {
    EEvents,
    customEventService,
} from '@/frontend/src/helpers/customEvent';
import { composeGeocodingPageViewPayload } from '@/frontend/src/helpers/tracking';
import { APP_ENV } from '@/frontend/src/helpers/env';
import { NAME_SPACE } from '@/frontend/src/hooks/useLoadTranslations';
import { isFrenchPortal } from '@/frontend/src/helpers/portal';

const DEFAULT_PLACE_TYPES_LIST = [EPlaceTypes.Street, EPlaceTypes.HouseNumber];

type TIconPosition = 'end' | 'start' | undefined;

const defaultAutocompleteItem: IAutocompleteItem = {
    city: '',
    houseNumber: '',
    id: '',
    label: '',
    name: '',
    postalCode: null,
    street: '',
    typeKey: undefined as EPlaceTypes | undefined,
};

const AddressAutocomplete = () => {
    const {
        geo,
        portal,
        addressAutocompleteError,
        mobileBreakpoint,
        shouldDisplayHouseNumber,
        isInvalidAddress,
        locationResult,
        parentApp,
    } = useAppContext();

    const dispatch = useAppDispatch();
    const [items, setItems] = useState<IAutocompleteItem[]>([]);
    const [isFetching, setIsFetching] = useState(false);
    const [autocomplete, setAutocomplete] = useState(defaultAutocompleteItem);
    const [hasSelectedAnAddress, setHasSelectedAnAddress] = useState(false);
    const [addressFieldBlurred, setAddressFieldBlurred] = useState(false);
    const { isMobile } = useBreakpoint(mobileBreakpoint);
    const { t } = useTranslation(NAME_SPACE);
    const autocompleteRef = useRef<HTMLInputElement>(null);
    const { language } = useLocale();

    const isExtraSmall = useBreakpointValue({
        base: true,
        sm: false,
    });

    useEffect(() => {
        dispatch({
            type: EActionType.SetIsInvalidAddress,
            payload: Boolean(
                (isMobile || addressFieldBlurred) &&
                    autocomplete.label &&
                    !isFetching &&
                    !hasSelectedAnAddress,
            ),
        });
    }, [
        isMobile,
        addressFieldBlurred,
        isFetching,
        hasSelectedAnAddress,
        autocomplete.label,
        dispatch,
    ]);

    useEffect(() => {
        dispatch({
            type: EActionType.SetShouldDisplayHouseNumber,
            payload: autocomplete.typeKey === EPlaceTypes.Street,
        });
    }, [autocomplete, dispatch]);

    useEffect(() => {
        debouncedFetchAddresses(autocomplete.label);

        return () => {
            debouncedFetchAddresses.cancel();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autocomplete.label]);

    useEffect(() => {
        const activeElement = autocompleteRef.current;

        const handleBlur = () => {
            setAddressFieldBlurred(true);
        };

        activeElement?.addEventListener('blur', handleBlur);

        return () => {
            activeElement?.removeEventListener('blur', handleBlur);
        };
    }, []);

    const getAddresses = async (text: string, callback: () => void) => {
        const params: IAutocompleteParams = {
            text,
            countries: getCountries(geo, portal).slice(0, 1),
            place_types: geo.placeTypes.length
                ? geo.placeTypes
                : DEFAULT_PLACE_TYPES_LIST,
            limit: geo.resultsLimit ?? 7,
            parent_types: [
                EPlaceTypes.Country,
                EPlaceTypes.Municipality,
                EPlaceTypes.Borough,
                EPlaceTypes.Street,
            ],
            // Filter for Metropolitan France only (exclude French overseas territories)
            ...(isFrenchPortal(portal) && { parent_ids: ['AD03FR2'] }),
        };

        const results = await fetchAddresses(params, language, portal);

        callback();

        return results;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedFetchAddresses = debounce(async (newValue: string) => {
        let results: IAutocompleteItem[] = [];

        if (newValue === '') {
            setItems([]);

            return;
        }

        try {
            results = await getAddresses(newValue, () => {
                setIsFetching(false);
            });
        } catch {
            dispatch({
                type: EActionType.SetAddressAutocompleteError,
                payload: true,
            });
            setIsFetching(false);
        }

        if (newValue) {
            setItems(results);
        }
    }, 300);

    const handleAddressChange = (newValue: string) => {
        if (newValue === autocomplete.label) {
            return;
        }

        setAutocomplete({
            ...defaultAutocompleteItem,
            label: newValue,
        });
        setHasSelectedAnAddress(false);
        setAddressFieldBlurred(false);

        dispatch({
            type: EActionType.SetShouldDisplayHouseNumber,
            payload: false,
        });
        dispatch({
            type: EActionType.SetLocationResult,
            payload: initialState.locationResult,
        });

        dispatch({
            type: EActionType.SetAddressAutocompleteError,
            payload: false,
        });

        try {
            if (newValue) {
                setIsFetching(true);

                if (!isExtraSmall) {
                    // do not apply for autocomplete optimized for mobile touch to avoid issues after selecting an address and editing it: https://avivgroup.atlassian.net/browse/VEGA-1780
                    setItems([]);
                }
            } else {
                debouncedFetchAddresses.cancel();
                setIsFetching(false);
                setItems([]);
            }
        } catch (e) {
            throw new Error('Get autocompleted addresses list failed');
        }
    };

    const handleAddressSubmit = (_value: string | null, key: string | null) => {
        const itemIndex = items.findIndex((option) => option.id === key);

        feedbackApiRequest(
            {
                input_string: autocomplete.label,
                position: itemIndex + 1,
                selected_id: items[itemIndex]?.id ?? '',
                subtype: 'COMPLETED',
                results_count: items.length,
            },
            portal,
        );

        const selectedOption = items.find((option) => option.id === key);

        if (selectedOption) {
            setAutocomplete(selectedOption);
            setHasSelectedAnAddress(true);
            dispatch({
                type: EActionType.SetShouldDisplayHouseNumber,
                payload: selectedOption.typeKey !== EPlaceTypes.HouseNumber,
            });

            const { city, houseNumber, id, postalCode, street, typeKey } =
                selectedOption;

            dispatch({
                type: EActionType.SetLocationResult,
                payload: {
                    id,
                    typeKey,
                    postalCode: postalCode ?? '',
                    city,
                    street,
                    houseNumber,
                },
            });
        }
    };

    const handleAddressBlur = () => {
        if (autocomplete.label && !hasSelectedAnAddress) {
            feedbackApiRequest(
                {
                    input_string: autocomplete.label,
                    subtype: 'EXIT',
                },
                portal,
            );
        }
    };

    const handleAddressClear = async () => {
        try {
            await feedbackApiRequest(
                {
                    input_string: autocomplete.label,
                    subtype: 'RESET',
                },
                portal,
            );
        } catch (error) {
            // do nothing
        }
    };

    const getHouseNumber = async (value: string) => {
        const countries = getCountries(geo, portal);
        const { street, city, postalCode, id } = autocomplete;

        const params = {
            number: value,
            address: street,
            city,
            postal_code: postalCode ?? '',
            ...(countries.length && {
                country: countries[0],
            }),
        };

        return fetchHouseNumber(params, id);
    };

    const debouncedFetchHouseNumber = debounce(async (value: string) => {
        const result: IGeoMatchItem | undefined = await getHouseNumber(value);

        let id,
            typeKey,
            houseNumber = '';

        if (result) {
            id = result.match.id;
            typeKey = result.match.type_key;
            houseNumber = value;
        } else {
            id = autocomplete.id;
            typeKey = autocomplete.typeKey;
        }

        dispatch({
            type: EActionType.SetLocationResult,
            payload: {
                ...locationResult,
                id,
                typeKey,
                houseNumber,
            },
        });
    }, 300);

    const handleHouseNumberChange = async (value: string) => {
        try {
            if (value) await debouncedFetchHouseNumber(value);
            else {
                // When user selects all the digits and then delete
                debouncedFetchHouseNumber.cancel();
                dispatch({
                    type: EActionType.SetLocationResult,
                    payload: {
                        ...locationResult,
                        id: autocomplete.id,
                        typeKey: autocomplete.typeKey,
                        houseNumber: '',
                    },
                });
            }
        } catch (e) {
            throw new Error('House number validation failed');
        }
    };

    const customOption = {
        label: t('common.fields.address.cannotFindMyAddressOption.label'),
        icon: ArrowRightIcon,
        iconPosition: 'end' as TIconPosition,
        onAction: () => {
            dispatch({
                type: EActionType.SetActiveView,
                payload: EActiveView.ClosestLocationSearch,
            });

            /* Closest location search isn't technically a page view. But on
            tracking team request, it is used to track click on cannotFindMyAddress*/
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (parentApp === EParentApp.EstimaLandingPage) {
                customEventService.fire(
                    EEvents.SEND_PAGE_VIEW,
                    composeGeocodingPageViewPayload({
                        env: APP_ENV,
                        lang: language,
                        portal,
                    }),
                );
            }
        },
    };

    return (
        <>
            <Box
                width="100%"
                unsafe_className="autocomplete"
                gridArea="autocomplete"
            >
                <Autocomplete
                    name="address"
                    ref={autocompleteRef} /* works only for desktop */
                    items={
                        autocomplete.label ? items : []
                    } /* avoid items to be displayed quickly when query is null */
                    label={t('common.fields.address.label')}
                    onChange={handleAddressChange}
                    onSubmit={handleAddressSubmit}
                    onBlur={handleAddressBlur}
                    onClear={handleAddressClear}
                    placeholder={t('common.fields.address.placeholder')}
                    shouldFocusFirstItem
                    isOptional
                    isFullWidth
                    hasNecessityIndicator={false}
                    menuTrigger="focus"
                    isLoading={!!autocomplete.label && isFetching}
                    loadingState={{
                        title: t('common.fields.address.loading.description'),
                    }}
                    error={
                        addressAutocompleteError
                            ? t('common.fields.address.error')
                            : undefined
                    }
                    {...(!!autocomplete.label && {
                        customItems: [customOption],
                    })}
                    autoComplete="off"
                />
            </Box>
            {shouldDisplayHouseNumber && (
                <Box
                    width="96px"
                    gridArea="houseNumber"
                    marginTop={isMobile ? 'spacing.16' : ''}
                >
                    <TextField
                        name="houseNumber"
                        label={t('common.fields.houseNumber.label')}
                        onChange={handleHouseNumberChange}
                        isFullWidth
                        isOptional
                        hasNecessityIndicator={false}
                        placeholder={t('common.fields.houseNumber.placeholder')}
                    />
                </Box>
            )}
            {(shouldDisplayHouseNumber || isInvalidAddress) && (
                <Box
                    gridArea="message"
                    marginTop={isMobile ? 'spacing.16' : ''}
                >
                    <FeedbackMessage variant="info">
                        {shouldDisplayHouseNumber
                            ? t('common.houseNumberInformationMessage')
                            : t('common.invalidAddressInformationMessage')}
                    </FeedbackMessage>
                </Box>
            )}
        </>
    );
};

export default AddressAutocomplete;
